import { ChangeEvent, FocusEvent, useCallback, useState } from 'react';
import { Columns, Element, Form, Icon } from 'react-bulma-components';
import { FaSort } from 'react-icons/fa6';
import { environment } from '../../../../environments/environment';
import { FormFieldData } from '../../../shared/form';
import Button from '../../../ui/button/button';
import FeedbackMessage from '../../../ui/feedback-message/feedback-message';
import styles from './invite-temporary-access-user-details-form.module.scss';

export type InviteTemporaryAccessUserFields = {
  email: string;
  emailConfirmation: string;
  daysDuration: number;
};

export type InviteTemporaryAccessUserDetailsFormData =
  FormFieldData<InviteTemporaryAccessUserFields>;

type InviteTemporaryUserFormChangeEvent = ChangeEvent<
  HTMLInputElement | HTMLSelectElement
>;

type InviteTemporaryUserFormBlurEvent = FocusEvent<
  HTMLInputElement | HTMLSelectElement,
  Element
>;

export type InviteTemporaryAccessUserDetailsFormValidation = Record<
  keyof InviteTemporaryAccessUserFields,
  ((value: string) => string | null) | null
>;

export type InviteUsersFormProps = {
  data: InviteTemporaryAccessUserDetailsFormData;
  onChangeData: (data: InviteTemporaryAccessUserDetailsFormData) => void;
  validate: InviteTemporaryAccessUserDetailsFormValidation;
  formError: string | null;
  submitButtonPressed: boolean;
};

const userProperties: {
  email: string;
  emailConfirmation: string;
  daysDuration: string;
} = {
  email: 'email',
  emailConfirmation: 'emailConfirmation',
  daysDuration: 'daysDuration',
};

const formFieldsLabels = {
  email: 'New user email',
  emailConfirmation: 'Confirm New user email',
  daysDuration: 'Duration',
};

const DEFAULT_PERMISSION_DAYS_DURATION = 10;
const MAX_PERMISSION_DAYS_DURATION = Number(
  environment.temporaryUserPermissionMaxDaysDuration
);

export function InviteTemporaryAccessUserDetailsForm({
  data: formData,
  onChangeData: setFormData,
  validate,
  formError,
  submitButtonPressed,
}: InviteUsersFormProps) {
  const [isDurationFieldActive, setIsDurationFieldActive] =
    useState<boolean>(false);

  const onInputChange = useCallback(
    (event: InviteTemporaryUserFormChangeEvent) => {
      const newValue = event.target.value;
      const name = event.target.name as keyof InviteTemporaryAccessUserFields;
      setFormData({
        ...formData,
        [name]: {
          ...formData[name],
          value: newValue,
          ...(submitButtonPressed && { error: null }),
          touched: true,
        },
      });
    },
    [formData, setFormData, submitButtonPressed]
  );

  const onBlur = useCallback(
    (event: InviteTemporaryUserFormBlurEvent) => {
      const value = event.target.value;
      const name = event.target.name as keyof InviteTemporaryAccessUserFields;
      const error = validate[name]?.(value);
      setFormData({
        ...formData,
        [name]: {
          ...formData[name],
          error,
        },
      });
    },
    [formData, setFormData, validate]
  );

  const onDaysDurationChange = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      let newValue = Number(formData.daysDuration.value);
      const rect = (event.target as HTMLElement).getBoundingClientRect();
      const mouseY = event.clientY - rect.top;
      const iconHeight = rect.height;
      const relativePosition = mouseY / iconHeight;
      if (relativePosition < 0.5) {
        newValue += 1;
      } else {
        newValue -= 1;
      }
      if (newValue > MAX_PERMISSION_DAYS_DURATION) {
        return MAX_PERMISSION_DAYS_DURATION;
      }
      if (newValue < 1) {
        return 1;
      }
      return newValue;
    },
    [formData.daysDuration.value]
  );

  return (
    <form>
      <Columns>
        <Columns.Column size="one-fifth">
          <Form.Label
            htmlFor="temporary-access-email"
            className="is-size-7 has-text-weight-normal"
          >
            {formFieldsLabels.email}
          </Form.Label>
          <Form.Field>
            <Form.Control>
              <Form.Input
                id="temporary-access-email"
                type="email"
                placeholder="mail@mail.com"
                className="is-size-7 placeholder-text-gray"
                name={userProperties.email}
                value={formData.email.value}
                onChange={onInputChange}
                onBlur={onBlur}
              />
            </Form.Control>
            <FeedbackMessage textClassName="is-size-7">
              {formData.email.error}
            </FeedbackMessage>
          </Form.Field>
        </Columns.Column>
        <Columns.Column size="one-fifth">
          <Form.Label
            htmlFor="temporary-access-email-confirmation"
            className="is-size-7 has-text-weight-normal"
          >
            {formFieldsLabels.emailConfirmation}
          </Form.Label>
          <Form.Field>
            <Form.Control>
              <Form.Input
                id="temporary-access-email-confirmation"
                type="emailConfirmation"
                placeholder="mail@mail.com"
                className="is-size-7 placeholder-text-gray"
                name={userProperties.emailConfirmation}
                value={formData.emailConfirmation.value}
                onChange={onInputChange}
                onBlur={onBlur}
              />
            </Form.Control>
            <FeedbackMessage textClassName="is-size-7">
              {formData.emailConfirmation.error}
            </FeedbackMessage>
          </Form.Field>
        </Columns.Column>
        <Columns.Column size="one-fifth">
          <Form.Label
            htmlFor="temporary-access-days-duration"
            className="is-size-7 has-text-weight-normal"
          >
            {formFieldsLabels.daysDuration}
          </Form.Label>
          <Columns>
            <Columns.Column
              size={'one-quarter'}
              className={styles.daysDurationInput}
            >
              <Form.Field
                id="daysDuration"
                className={styles.daysDurationField}
              >
                <Form.Control
                  onFocus={() => setIsDurationFieldActive(true)}
                  onBlur={() => setIsDurationFieldActive(false)}
                  onMouseEnter={() => setIsDurationFieldActive(true)}
                  onMouseLeave={() => setIsDurationFieldActive(false)}
                  display="flex"
                  flexDirection="column"
                >
                  <Form.Input
                    id="temporary-access-days-duration"
                    className="is-size-7"
                    name="daysDuration"
                    type="number"
                    min={1}
                    max={MAX_PERMISSION_DAYS_DURATION}
                    mr={4}
                    value={
                      formData.daysDuration.value ??
                      DEFAULT_PERMISSION_DAYS_DURATION
                    }
                    onChange={(event) => {
                      if (
                        event.target.value !== '' &&
                        Number(event.target.value) < 1
                      ) {
                        event.target.value = '1';
                      }
                      onInputChange(event);
                    }}
                  />
                  <Button
                    className={`is-size-7 ${styles.buttonContainer}`}
                    text
                    type="button"
                    onClick={(event) => {
                      const newValue = onDaysDurationChange(event);
                      setFormData({
                        ...formData,
                        daysDuration: {
                          ...formData.daysDuration,
                          value: newValue.toString(),
                          touched: true,
                        },
                      });
                    }}
                    tabIndex={-1}
                    aria-hidden="true"
                  >
                    <Icon align="right" className={styles.iconContainer}>
                      <FaSort
                        className={
                          isDurationFieldActive
                            ? styles.chevronActive
                            : styles.chevron
                        }
                      />
                    </Icon>
                  </Button>
                </Form.Control>
              </Form.Field>
            </Columns.Column>
            <Columns.Column
              className={styles.daysDurationLabel}
              alignContent="center"
            >
              <Element>
                <span className="is-size-6">days</span>
              </Element>
            </Columns.Column>
          </Columns>
        </Columns.Column>
      </Columns>

      <FeedbackMessage textClassName="is-size-7">{formError}</FeedbackMessage>
    </form>
  );
}

export default InviteTemporaryAccessUserDetailsForm;
